import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { DURUM_RENKLERI, TODAY } from '@/lib/constants';

interface Reservation {
  id: number;
  rezervasyon_no: string;
  musteri_adi: string;
  telefon: string;
  gelis_tarihi: string;
  gelis_saati: string;
  alinis_saati: string | null;
  ucak_kodu: string;
  alinis_yeri: string;
  birak_yeri: string;
  kisi_sayisi: number;
  arac_tipi: string;
  transfer_tipi: string;
  ucret: number;
  para_birimi: string;
  atanan_arac: string | null;
  atanan_plaka: string | null;
  durum: string;
  ilce: string;
}

export default function YaklasanPage() {
  useAuthGuard();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aktifSekme, setAktifSekme] = useState<'7gun' | '30gun' | 'tumu'>('7gun');
  const [uyariFilter, setUyariFilter] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Reservation | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('reservations')
      .select('id,rezervasyon_no,musteri_adi,telefon,gelis_tarihi,gelis_saati,alinis_saati,ucak_kodu,alinis_yeri,birak_yeri,kisi_sayisi,arac_tipi,transfer_tipi,ucret,para_birimi,atanan_arac,atanan_plaka,durum,ilce')
      .gt('gelis_tarihi', TODAY)
      .neq('durum', 'İptal Edildi')
      .order('gelis_tarihi', { ascending: true });
    if (err) setError(err.message);
    else setReservations(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const gunFarki = (tarih: string) => {
    const diff = Math.ceil((new Date(tarih).getTime() - new Date(TODAY).getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 1) return 'Yarın';
    if (diff === 2) return '2 gün sonra';
    return `${diff} gün sonra`;
  };

  const getDiff = (tarih: string) => Math.ceil((new Date(tarih).getTime() - new Date(TODAY).getTime()) / (1000 * 60 * 60 * 24));

  const uyariBul = (r: Reservation) => {
    const uyarilar: string[] = [];
    if (getDiff(r.gelis_tarihi) <= 1) uyarilar.push('24 saatten az kaldı');
    if (!r.atanan_arac) uyarilar.push('Araç atanmamış');
    if (r.durum === 'Ödeme Bekliyor') uyarilar.push('Ödeme bekliyor');
    if (!r.ucak_kodu) uyarilar.push('Uçak kodu eksik');
    return uyarilar;
  };

  const filtreli = useMemo(() => {
    let list = [...reservations];
    if (aktifSekme === '7gun') list = list.filter(r => getDiff(r.gelis_tarihi) <= 7);
    else if (aktifSekme === '30gun') list = list.filter(r => getDiff(r.gelis_tarihi) <= 30);
    if (uyariFilter) list = list.filter(r => uyariBul(r).length > 0);
    return list;
  }, [reservations, aktifSekme, uyariFilter]);

  const handleDurumDegis = async (id: number, yeniDurum: string) => {
    setUpdatingId(id);
    const { error: err } = await supabase.from('reservations').update({ durum: yeniDurum }).eq('id', id);
    if (!err) setReservations(prev => prev.map(r => r.id === id ? { ...r, durum: yeniDurum } : r));
    setUpdatingId(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const idToDelete = deleteConfirm.id;
    setDeleting(true);
    setDeleteConfirm(null);

    const { data, error } = await supabase.functions.invoke('manage-reservations', {
      body: { action: 'delete', id: idToDelete },
    });

    setDeleting(false);

    if (error || !data?.success) {
      setError('Silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
      await fetchData();
      return;
    }
    setReservations(prev => prev.filter(r => r.id !== idToDelete));
  };

  const yedi = reservations.filter(r => getDiff(r.gelis_tarihi) <= 7).length;
  const yarin = reservations.filter(r => getDiff(r.gelis_tarihi) <= 1).length;
  const atanmamis = reservations.filter(r => !r.atanan_arac).length;
  const odeme = reservations.filter(r => r.durum === 'Ödeme Bekliyor').length;

  return (
    <AdminLayout>
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-vip-dark border border-red-500/20 rounded-xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-white text-center mb-1">Rezervasyonu Sil</h3>
            <p className="text-xs text-vip-gold text-center mb-3">{deleteConfirm.rezervasyon_no}</p>
            <p className="text-sm text-gray-400 text-center mb-5">Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white transition-colors whitespace-nowrap">İptal</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50 whitespace-nowrap">
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">Yaklaşan Rezervasyonlar</h2>
          <p className="text-sm text-gray-400 mt-1">Gelecek rezervasyonları takip edin ve uyarıları yönetin</p>
        </div>
        <a href="/rezervasyonlar/yeni" className="px-4 py-2 bg-vip-gold text-vip-black text-sm font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap text-center">
          <i className="ri-add-line mr-1"></i>Yeni Rezervasyon
        </a>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={fetchData} className="ml-auto text-xs text-red-400 underline">Tekrar dene</button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-white">{yedi}</p>
          <p className="text-xs text-gray-400">Önümüzdeki 7 Gün</p>
        </div>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{yarin}</p>
          <p className="text-xs text-gray-400">Yarın / 24 Saat</p>
        </div>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-400">{atanmamis}</p>
          <p className="text-xs text-gray-400">Araç Atanmamış</p>
        </div>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-orange-400">{odeme}</p>
          <p className="text-xs text-gray-400">Ödeme Bekliyor</p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-3 mb-5">
        <div className="flex gap-1 bg-vip-dark border border-vip-gold/10 rounded-lg p-1">
          {(['7gun', '30gun', 'tumu'] as const).map((s) => (
            <button key={s} onClick={() => setAktifSekme(s)}
              className={`px-4 py-2 text-xs font-medium rounded-md transition-all whitespace-nowrap ${aktifSekme === s ? 'bg-vip-gold/15 text-vip-gold border border-vip-gold/20' : 'text-gray-400 hover:text-white'}`}>
              {s === '7gun' ? 'Önümüzdeki 7 Gün' : s === '30gun' ? 'Önümüzdeki 30 Gün' : 'Tümü'}
            </button>
          ))}
        </div>
        <button onClick={() => setUyariFilter(!uyariFilter)}
          className={`px-4 py-2 text-xs font-medium rounded-md border transition-all whitespace-nowrap ${uyariFilter ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' : 'bg-vip-dark text-gray-400 border-vip-gold/10 hover:text-white'}`}>
          <i className="ri-error-warning-line mr-1"></i>Uyarıları Göster
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4 animate-pulse flex gap-4">
              <div className="w-14 h-14 bg-vip-surface rounded-lg flex-shrink-0"></div>
              <div className="flex-1 space-y-2"><div className="h-4 bg-vip-surface rounded w-1/4"></div><div className="h-3 bg-vip-surface rounded w-1/2"></div><div className="h-3 bg-vip-surface rounded w-1/3"></div></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filtreli.map(r => {
            const uyarilar = uyariBul(r);
            return (
              <div key={r.id} className={`bg-vip-dark border rounded-lg p-4 md:p-5 ${uyarilar.length > 0 ? 'border-amber-500/20' : 'border-vip-gold/10'}`}>
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
                  <div className="flex-shrink-0 w-14 text-center">
                    <p className="text-lg font-bold text-vip-gold">{r.gelis_tarihi.split('-')[2]}</p>
                    <p className="text-[10px] text-gray-500">{new Date(r.gelis_tarihi).toLocaleDateString('tr-TR', { month: 'short' })}</p>
                    <p className="text-[10px] text-amber-400 mt-0.5">{gunFarki(r.gelis_tarihi)}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <p className="text-sm font-medium text-white">{r.musteri_adi}</p>
                      <span className="text-xs text-gray-500">{r.telefon}</span>
                      <span className="text-xs text-gray-500">{r.gelis_saati?.slice(0, 5)} {r.alinis_saati && <span className="text-vip-gold">({String(r.alinis_saati).slice(0, 5)})</span>}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400 mb-1.5">
                      <span>{r.alinis_yeri}</span>
                      <i className="ri-arrow-right-line text-vip-gold"></i>
                      <span>{r.birak_yeri || 'Yok'}</span>
                      {r.ucak_kodu && <span className="text-gray-500 ml-2">{r.ucak_kodu}</span>}
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                      <span>{r.arac_tipi}</span>
                      <span>{r.kisi_sayisi} Kişi</span>
                      <span>{r.transfer_tipi}</span>
                      <span className="text-vip-gold">{r.ucret} {r.para_birimi}</span>
                    </div>
                    {(r.atanan_arac || r.atanan_plaka) && (
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500 mt-1">
                        {r.atanan_arac && <span className="text-vip-gold"><i className="ri-roadster-line mr-1"></i>{r.atanan_arac}</span>}
                        {r.atanan_plaka && <span className="font-mono text-gray-500">{r.atanan_plaka}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      {updatingId === r.id ? (
                        <div className="w-5 h-5 border border-vip-gold/40 border-t-vip-gold rounded-full animate-spin"></div>
                      ) : (
                        <select value={r.durum} onChange={e => handleDurumDegis(r.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-md border bg-transparent cursor-pointer ${DURUM_RENKLERI[r.durum] || 'text-gray-400 border-gray-500/30'}`}>
                          {['Yeni Rezervasyon','Onaylandı','Araç Atandı','Transfer Tamamlandı','İptal Edildi','Ödeme Bekliyor'].map(d => (
                            <option key={d} value={d} className="bg-vip-dark text-white">{d}</option>
                          ))}
                        </select>
                      )}
                      <a href={`/rezervasyonlar/yeni?edit=${r.id}`} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center">
                        <i className="ri-edit-line text-xs"></i>
                      </a>
                      <button onClick={() => setDeleteConfirm(r)} className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center">
                        <i className="ri-delete-bin-line text-xs"></i>
                      </button>
                    </div>
                    {uyarilar.length > 0 && (
                      <div className="flex flex-wrap gap-1 justify-end">
                        {uyarilar.map((u, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded border border-amber-500/20 whitespace-nowrap">
                            <i className="ri-error-warning-line mr-0.5"></i>{u}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && filtreli.length === 0 && (
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-8 text-center">
          <p className="text-sm text-gray-400">Filtreye uygun yaklaşan rezervasyon bulunamadı.</p>
        </div>
      )}
    </AdminLayout>
  );
}